<template>
  <Dialog
    visible
    :header="pageTitle"
    modal
    :style="{ width: '90vw', height: '90vh' }"
    :closable="false"
    :dismissable-mask="false"
    block-scroll
    :draggable="false"
  >
    <template #default>
      <div v-if="!queryLoading && notFound">
        <p class="error-message">The cohort you are looking for does not exist.</p>
        <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="onDialogClose" />
      </div>
      <div v-else>
        <VdForm
          :back-link="backLink"
          :form="form"
          :form-changed="formChanged"
          :loading="queryLoading"
          :save-loading="saveLoading"
          @submit="saveForm"
          @cancel="onDialogClose"
        >
          <div class="form-section">
            <VdFormField v-model="form.name" prop="name" label="Name" type="text" required />
            <VdFormField v-model="form.description" prop="description" label="Description" type="textarea" />
          </div>
          <div v-if="isShopAbTest" class="shop-overrides-wrapper">
            <label :class="['class-selection-option', { locked: !!getLayerLockingClass() }]">
              Shop
              <VdTooltip
                v-if="!!getLayerLockingClass()"
                :content="`The shop is already used by the layer '${getLayerLockingClass()}'`"
                orientation="right"
                alignment="end"
              >
                <VdIcon store="fa" name="lock" />
              </VdTooltip>
            </label>

            <Select
              v-if="form.overrides"
              v-model="form.overrides[0].instances[0].values[0].value"
              :options="shopsToDisplay"
              :show-clear="false"
              option-value="id"
              option-label="name"
              :disabled="!!getLayerLockingClass()"
            />
          </div>

          <div v-if="classChoices.length && !isShopAbTest" class="class-selection">
            <VdFormField prop="class">
              <VdSelect v-model="selectedClassTechnicalName" :choices="classChoices" placeholder="Add a class" multiple>
                <template #item="{ item }">
                  <div :class="['class-selection-option', { locked: item.locked }]">
                    <template v-if="item.locked">
                      <VdTooltip
                        :content="`This class is already used by the layer '${item.locked}'`"
                        orientation="right"
                        alignment="end"
                      >
                        <VdIcon store="fa" name="lock" />

                        <span>{{ item.label }}</span>
                      </VdTooltip>
                    </template>

                    <span v-else>{{ item.label }}</span>
                  </div>
                </template>
              </VdSelect>
            </VdFormField>
          </div>

          <div v-for="override in form.overrides" :key="override.classId" style="margin-bottom: 1rem">
            <CohortFormClassItem
              :cohort-id="cohortId"
              :clazz="classesByTechnicalName[override.classId]"
              :override-instances="override.instances"
              :shops="shopsToDisplay"
              :model-value="override"
              :overridden-values="instanceValuesByClass[override.classId]"
              @update:model-value="updateOverriddenClassValues"
              @delete="() => onDeleteClass(override.classId)"
            />

            <div v-if="classesByTechnicalName[override.classId].dynamicInstances" class="add-instance-button-wrapper">
              <vd-button
                type="secondary"
                skin="blue"
                icon-left="fa:add"
                class="add-instance-button"
                @click="onAddInstance(override.classId)"
              >
                Add instance
              </vd-button>
            </div>
          </div>
        </VdForm>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Select from "primevue/select";
import { v4 as uuidv4 } from "uuid";
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";

import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { typeToDefaultValueMapping } from "@/constants";
import {
  CohortInput,
  CohortInputOverride,
  queryNames,
  useCohortFormPageQuery,
  useCohortFormPageSaveMutation,
  useMutationResponse,
} from "@/graphql";
import useFormTracking from "@/hooks/form-tracking";
import useMessages from "@/hooks/messages";
import useNotifications from "@/hooks/notifications";
import useShopsManager from "@/modules/Store/hooks/useShopsManager";
import links from "@/router/links";
import { objectifyArray } from "@/utils";
import { VdForm, VdFormField, VdSelect, VdTooltip } from "@voodoo.io/voodoo-ui";
import CohortFormClassItem from "./components/CohortFormClassItem/CohortFormClassItem.vue";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  abtestId: {
    type: String,
    required: true,
  },
  cohortId: {
    type: String,
    default: null,
  },
  layerId: {
    type: String,
    default: null,
  },
  preview: {
    type: Boolean,
    default: false,
  },
  duplicate: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const { confirm } = useMessages();
const { notifyError } = useNotifications();

const selectedTab = ref("");
const form = ref<CohortInput>({
  name: "",
  description: "",
  allocationRate: 0,
  overrides: [],
});

const { formChanged, resetTracking: resetFormTracking } = useFormTracking(form.value);

const { result, loading: queryLoading } = useCohortFormPageQuery({
  gameId: props.gameId,
  versionId: props.versionId,
  abtestId: props.abtestId,
});

const abTest = computed(() => result.value?.game?.version?.abtest ?? undefined);
const cohorts = computed(() => result.value?.game?.version?.abtest?.cohorts ?? []);
const layerId = computed(() => result.value?.game?.version?.abtest?.layerId ?? undefined);
const layers = computed(() => result.value?.game?.version?.layers ?? []);
const cohort = computed(() => cohorts.value.find(({ id }) => id === props.cohortId));
const notFound = computed(() => props.cohortId && !queryLoading.value && !cohort.value);
const classes = computed(() => result.value?.game?.version?.classes ?? []);
const abTestType = computed(() => abTest.value?.treatmentTypes?.[0] ?? "class");
const classesByTechnicalName = computed(() =>
  Object.fromEntries(classes.value.map((clazz) => [clazz.technicalName, clazz]))
);

const pageTitle = computed(() => {
  if (props.cohortId) {
    const title = cohort.value?.name ?? "";
    if (props.duplicate) {
      return `Duplicate of ${title}`;
    }
    return title;
  }
  return "New cohort";
});

const instanceValuesByClass = computed(() => {
  if (!cohort.value) {
    return {};
  }
  return objectifyArray(
    cohort.value.overrides.map((override) => ({
      technicalName: override.class.technicalName,
      instances: override.instances,
    })),
    ["technicalName", "instances", ["id", "values", ["technicalName", "*"]]]
  );
});

watch(
  cohort,
  (watchedCohort) => {
    if (watchedCohort) {
      form.value.name = props.duplicate ? `Duplicate of ${watchedCohort.name}` : watchedCohort.name;
      form.value.description = watchedCohort.description;
      form.value.allocationRate = props.duplicate ? 0 : watchedCohort.allocationRate;

      form.value.overrides = watchedCohort.overrides.map((override) => {
        const classTechnicalName = override.class.technicalName;
        const classDefinition = classesByTechnicalName.value[classTechnicalName];
        const overriddenValues = instanceValuesByClass.value[classTechnicalName] ?? {};

        return {
          classId: classTechnicalName,
          instances: (classDefinition?.instances ?? []).map((inst) => ({
            id: inst.id,
            enabled: overriddenValues[inst.id]
              ? override.instances.find((i) => i.id === inst.id)?.enabled ?? true
              : inst.enabled,
            values: inst.values.map(({ technicalName: attrTechnicalName, defaultValue }) => ({
              technicalName: attrTechnicalName,
              value: overriddenValues[inst.id]?.[attrTechnicalName]?.value ?? defaultValue,
            })),
          })),
        };
      });

      if (form.value.overrides.length) {
        selectedTab.value = form.value.overrides[0].classId;
      }

      resetFormTracking();
    }
  },
  { immediate: true }
);

const classChoices = computed(() =>
  classes.value
    .filter(({ technicalName }) => !form.value.overrides?.find((item) => item.classId === technicalName))
    .filter(({ technicalName }) => technicalName !== "VT.ShopManagementClass")
    .map(({ displayName, technicalName, lockedByLayer }) => {
      let disabled = false;
      const locked =
        !!lockedByLayer && lockedByLayer !== layerId.value
          ? layers.value.find((l) => l.id === lockedByLayer)?.name
          : undefined;
      disabled = (lockedByLayer && lockedByLayer !== layerId.value) || false;

      return {
        label: displayName,
        value: technicalName,
        locked,
        disabled,
      };
    })
);

const selectedClassTechnicalName = ref("");

watch(selectedClassTechnicalName, async (watchedSelectedClassTechnicalName) => {
  const firstWatchedValue = watchedSelectedClassTechnicalName[0];
  if (!firstWatchedValue) {
    return;
  }
  const selectedClass = classes.value.find(({ technicalName }) => technicalName === firstWatchedValue);

  if (!selectedClass) {
    throw new Error(`unable to find the class ${firstWatchedValue}`);
  }
  const overriddenClass = instanceValuesByClass.value[selectedClass.technicalName];

  if (!form.value.overrides) {
    form.value.overrides = [];
  }

  form.value.overrides = [
    ...form.value.overrides,
    {
      classId: selectedClass.technicalName,
      instances: selectedClass.instances.map((instance) => {
        const overriddenInstance = overriddenClass ? overriddenClass[instance.id] : null;
        const instanceDefaultValuesByAttribute = Object.fromEntries(
          instance.values.map(({ technicalName, defaultValue }) => [technicalName, defaultValue])
        );
        return {
          id: instance.id,
          enabled: instance.enabled,
          values: instance.values.map(({ technicalName: attrTechnicalName }) => {
            const overriddenValue = overriddenInstance
              ? overriddenInstance[attrTechnicalName].value
              : instanceDefaultValuesByAttribute[attrTechnicalName];
            return {
              technicalName: attrTechnicalName,
              value: overriddenValue,
            };
          }),
        };
      }),
    },
  ];

  selectedTab.value = selectedClass.technicalName;
  await nextTick();
  selectedClassTechnicalName.value = "";
});

const onDeleteClass = async (technicalName: string) => {
  const confirmed = await confirm("Do you really want to remove this class from the list?");
  if (confirmed) {
    form.value.overrides = form.value.overrides?.filter((item) => item.classId !== technicalName);
    if (form.value.overrides?.length) {
      selectedTab.value = form.value.overrides[form.value.overrides.length - 1].classId;
    }
  }
};

const updateOverriddenClassValues = (value: CohortInputOverride) => {
  form.value.overrides = form.value.overrides?.map((item) => (item.classId === value.classId ? value : item));
};

const backLink = {
  name: links.games.abtests.cohorts.list,
  params: {
    gameId: props.gameId,
    abtestId: props.abtestId,
    forceReload: "true",
  },
};

const { mutate: save, loading: saveLoading } = useCohortFormPageSaveMutation({
  refetchQueries: [queryNames.CohortsListPage],
});

const isShopAbTest = computed(() => abTestType.value === "shop");

const getLayerLockingClass = () => {
  const shopClass = classes.value.find((item) => item.technicalName === "VT.ShopManagementClass");
  if (!shopClass) {
    return false;
  }
  const { lockedByLayer } = shopClass;
  return !!lockedByLayer && lockedByLayer !== layerId.value
    ? layers.value.find((l) => l.id === lockedByLayer)?.name
    : undefined;
};

const saveForm = async () => {
  if (isShopAbTest.value && getLayerLockingClass()) {
    notifyError("Ensure that the shop is not locked by any other layer", {
      dangerouslyUseHTMLString: true,
      duration: 3 * 1000,
    });
    return;
  }
  const response = await save({
    gameId: props.gameId,
    versionId: props.versionId,
    abtestId: props.abtestId,
    cohortId: props.duplicate ? null : props.cohortId,
    input: form.value,
  });

  const data = useMutationResponse(response, (resp) => resp?.saveCohort);
  if (data?.success) {
    router.push(backLink);
  }
};

const onDialogClose = () => {
  router.push(backLink);
};

const onAddInstance = (id: string) => {
  form.value.overrides = (form.value?.overrides || []).map((override) =>
    override.classId === id
      ? {
          ...override,
          instances: [
            ...override.instances,
            {
              id: uuidv4(),
              enabled: true,
              values: classesByTechnicalName.value[id].attributes.map(({ technicalName, formFieldType }) => {
                // let value: string | boolean | string[] = isArrayType ? [] : "";
                // value = formFieldType === "boolean" ? false : value;
                const value = typeToDefaultValueMapping[formFieldType];
                return {
                  technicalName,
                  value,
                };
              }),
            },
          ],
        }
      : override
  );
};

const { shopsToDisplay } = useShopsManager(props.gameId, props.versionId);

watch(
  () => isShopAbTest.value,
  (isShopAbTestUpdate) => {
    if (props.duplicate) {
      return;
    }
    if (isShopAbTestUpdate) {
      selectedClassTechnicalName.value = "VT.ShopManagementClass";
    } else {
      selectedClassTechnicalName.value = "";
    }
  },
  {
    immediate: true,
  }
);
</script>

<style scoped lang="scss">
:deep(.el-form-item__label) {
  text-align: left;
}
</style>

<style module lang="scss"></style>

<style lang="scss">
.class-selection-option {
  &.locked {
    color: $red-alert;

    svg {
      color: $red-alert;
      margin-right: size("XS");
    }
  }
}

.add-instance-button-wrapper {
  background: var(--vd-color-text-light-9);
  border: 1px solid var(--vd-color-text-light-7);
  border-top: none;
  padding: 0.5rem;
}

.cohort-form-page {
  .vd-error-component {
    margin-top: 30%;
    max-width: 100%;
  }

  .el-tabs--left .el-tabs__header.is-left {
    width: 20%;
    min-width: 200px;
  }

  .el-tabs--left .el-tabs__item.is-left {
    @include ellipsis;
  }

  .class-selection {
    display: flex;
    align-items: center;

    .add-class-button {
      margin-left: size("M");
    }
  }

  .shop-overrides-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .p-dropdown {
      width: 300px;
    }
  }

  .add-instance-button {
    margin-top: 1rem;
  }
}
</style>
